/**
 * Service for fetching help articles from the backend
 */

const getHelpArticleUrl = (): string => {
  const envAny = (import.meta as any).env || {};
  const projectId = envAny.VITE_FIREBASE_PROJECT_ID || 'lemur-476302';
  
  // Check for direct URL override
  if (envAny.VITE_GET_HELP_ARTICLE_URL) {
    return envAny.VITE_GET_HELP_ARTICLE_URL;
  }
  
  // Check for base URL
  const base = envAny.VITE_FUNCTIONS_BASE;
  if (base) {
    return `${base}/getHelpArticle`;
  }
  
  // Auto-detect: check if we're on localhost (use emulator) or production
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.startsWith('192.168.');
  
  if (isLocalhost) {
    // Try local emulator first
    return `http://localhost:5001/${projectId}/us-central1/getHelpArticle`;
  }
  
  // Production: use deployed functions URL
  return `https://us-central1-${projectId}.cloudfunctions.net/getHelpArticle`;
};

const getListHelpArticlesUrl = (): string => {
  const envAny = (import.meta as any).env || {};
  const projectId = envAny.VITE_FIREBASE_PROJECT_ID || 'lemur-476302';
  
  // Check for direct URL override
  if (envAny.VITE_LIST_HELP_ARTICLES_URL) {
    return envAny.VITE_LIST_HELP_ARTICLES_URL;
  }
  
  // Check for base URL
  const base = envAny.VITE_FUNCTIONS_BASE;
  if (base) {
    return `${base}/listHelpArticles`;
  }
  
  // Auto-detect
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.startsWith('192.168.');
  
  if (isLocalhost) {
    return `http://localhost:5001/${projectId}/us-central1/listHelpArticles`;
  }
  
  return `https://us-central1-${projectId}.cloudfunctions.net/listHelpArticles`;
};

export interface HelpArticle {
  id: string;
  title: string;
  category: string;
  content: string;
  relatedFeatures?: string[];
}

export interface HelpArticleSummary {
  id: string;
  title: string;
  category: string;
}

/**
 * Fetch a help article by ID
 */
export async function getHelpArticle(articleId: string): Promise<HelpArticle> {
  const url = `${getHelpArticleUrl()}?id=${encodeURIComponent(articleId)}`;
  
  console.log('[helpService] Fetching article from:', url);
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      console.error('[helpService] API error:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        url,
      });
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    // If it's a network error (CORS, connection refused, etc.), provide helpful message
    if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
      throw new Error(`Cannot connect to help service. The functions may not be deployed yet. Error: ${error.message}`);
    }
    throw error;
  }
}

/**
 * List all help articles, optionally filtered by category
 */
export async function listHelpArticles(category?: string): Promise<HelpArticleSummary[]> {
  const baseUrl = getListHelpArticlesUrl();
  const url = category 
    ? `${baseUrl}?category=${encodeURIComponent(category)}`
    : baseUrl;
  
  console.log('[helpService] Listing articles from:', url);
  
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to list articles: ${response.status} ${errorText}`);
  }
  
  return await response.json();
}

